import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { supabase } from '../services/supabase'
import { useOrganizationId } from '../hooks/useOrganizationId'
import Card from '../components/Card'
import Button from '../components/Button'
import ClientTags from '../components/ClientTags'
import ClientCommunications from '../components/ClientCommunications'
import ClientDocuments from '../components/ClientDocuments'
import ClientMerge from '../components/ClientMerge'
import HistoryComparisonView from '../components/HistoryComparisonView'
import HistoryTimeline from '../components/HistoryTimeline'
import FollowUpReminders from '../components/FollowUpReminders'
import ExaminationComparison from '../components/ExaminationComparison'
import {
  ArrowLeft,
  FileText,
  Heart,
  Calendar,
  Activity,
  Stethoscope,
  EyeOff,
  Eye,
  X,
  Tag,
  MessageSquare,
  FolderOpen,
  Users,
  GitCompare,
  Clock,
  Bell,
  Download,
  Upload,
} from 'lucide-react'
import { formatDate } from '../utils/helpers'
import ExaminationImportExport from '../components/ExaminationImportExport'
import ExaminationPdfEditor from '../components/ExaminationPdfEditor'

const ClientDetails = () => {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()
  const organizationId = useOrganizationId()
  const [loading, setLoading] = useState(true)
  const [client, setClient] = useState(null)
  const [carePlans, setCarePlans] = useState([])
  const [therapies, setTherapies] = useState([])
  const [sessions, setSessions] = useState([])
  const [examinations, setExaminations] = useState([])
  const [selectedExam, setSelectedExam] = useState(null)
  const [privacyMode, setPrivacyMode] = useState(true)
  const [error, setError] = useState('')
  const [activeTab, setActiveTab] = useState('overview')
  const [clientTags, setClientTags] = useState([])
  const [medicalHistory, setMedicalHistory] = useState(null)
  const [showExamCsvTransfer, setShowExamCsvTransfer] = useState(false)
  const [pdfEditorOpen, setPdfEditorOpen] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)

  useEffect(() => {
    if (!id || !organizationId) return
    const load = async () => {
      setLoading(true)
      setError('')
      try {
        const [{ data: clientData, error: clientError }] = await Promise.all([
          supabase.from('clients').select('*').eq('id', id).eq('organization_id', organizationId).single(),
        ])
        if (clientError) throw clientError
        setClient(clientData)

        const [
          planResponse,
          therapyResponse,
          sessionResponse,
          examResponse,
          historyResponse,
        ] = await Promise.all([
          supabase
            .from('care_plans')
            .select('*')
            .or(`client_id.eq.${id},and(client_id.is.null,archived_at.is.not.null)`)
            .eq('organization_id', organizationId)
            .order('created_at', { ascending: false }),
          supabase
            .from('therapies')
            .select('*')
            .or(`client_id.eq.${id},and(client_id.is.null,archived_at.is.not.null)`)
            .eq('organization_id', organizationId)
            .order('scheduled_date', { ascending: true }),
          supabase
            .from('sessions')
            .select('*')
            .or(`client_id.eq.${id},and(client_id.is.null,archived_at.is.not.null)`)
            .eq('organization_id', organizationId)
            .order('session_date', { ascending: false }),
          supabase
            .from('examinations')
            .select('*, medical_history:medical_history_id (*)')
            .eq('client_id', id)
            .eq('organization_id', organizationId)
            .order('created_at', { ascending: false }),
          supabase
            .from('medical_histories')
            .select('*')
            .eq('client_id', id)
            .eq('organization_id', organizationId)
            .order('created_at', { ascending: false })
            .limit(1)
            .single(),
        ])

        if (planResponse?.error) console.error(planResponse.error)
        if (therapyResponse?.error) console.error(therapyResponse.error)
        if (sessionResponse?.error) console.error(sessionResponse.error)
        if (examResponse?.error) console.error(examResponse.error)
        if (historyResponse?.error && historyResponse.error.code !== 'PGRST116') {
          console.error(historyResponse.error)
        }

        setCarePlans(planResponse?.data || [])
        setTherapies(therapyResponse?.data || [])
        setSessions(sessionResponse?.data || [])
        setExaminations(examResponse?.data || [])
        setMedicalHistory(historyResponse?.data || null)
      } catch (err) {
        setError(err.message || 'Unable to load client details.')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [id, organizationId, refreshKey])

  const latestExam = examinations[0] || null
  const sensitiveClass = privacyMode ? 'blur-sm pointer-events-none select-none' : ''

  const privacyBanner = (
    <div className="glass-card p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 w-full min-w-0">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">{t('clients.details.privacyShield')}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 break-words">
          {t('clients.details.privacyShieldDescription', { status: privacyMode ? t('clients.details.obscured') : t('clients.details.visible') })}
        </p>
      </div>
      <Button
        variant={privacyMode ? 'secondary' : 'primary'}
        onClick={() => setPrivacyMode((prev) => !prev)}
        className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto text-sm sm:text-base px-3 sm:px-6 py-2 sm:py-3"
      >
        {privacyMode ? <Eye size={16} className="sm:w-[18px] sm:h-[18px]" /> : <EyeOff size={16} className="sm:w-[18px] sm:h-[18px]" />}
        {privacyMode ? t('clients.details.revealData') : t('clients.details.maskData')}
      </Button>
    </div>
  )

  if (loading) {
    return (
      <div className="section-container">
        <p className="text-gray-500 dark:text-gray-400">{t('clients.details.loading')}</p>
      </div>
    )
  }

  if (!client) {
    return (
      <div className="section-container">
        <p className="text-red-500 dark:text-red-400">{t('clients.details.notFound')}</p>
        <Button className="mt-4" onClick={() => navigate('/clients')}>
          {t('clients.details.backToClients')}
        </Button>
      </div>
    )
  }

  return (
    <div className="section-container space-y-6 sm:space-y-8 w-full max-w-full min-w-0">
      <div className="flex flex-col gap-4 w-full min-w-0">
        <Button variant="secondary" className="w-fit flex items-center gap-2 text-sm sm:text-base px-3 sm:px-6 py-2 sm:py-3" onClick={() => navigate(-1)}>
          <ArrowLeft size={16} className="sm:w-[18px] sm:h-[18px]" />
          {t('clients.details.back')}
        </Button>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`flex flex-col gap-2 ${sensitiveClass}`}
        >
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white break-words">
            {client.first_name} {client.last_name}
          </h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 break-words">
            {client.email} • {client.phone || t('clients.details.noPhone')}
          </p>
          {client.date_of_birth && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('clients.details.dob')}: {new Date(client.date_of_birth).toLocaleDateString()}
            </p>
          )}
        </motion.div>
        {error && <p className="text-red-500 text-sm">{error}</p>}
        {privacyBanner}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4 w-full min-w-0">
        <Card variant="soft" className={`p-4 sm:p-6 ${sensitiveClass} w-full min-w-0`}>
          <div className="flex items-center gap-3 mb-4 min-w-0">
            <Stethoscope className="text-primary-600 flex-shrink-0" />
            <div className="flex-1 flex items-center justify-between min-w-0">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white truncate min-w-0">{t('clients.details.latestExamination')}</h2>
              {latestExam && (
                <button
                  onClick={() => setSelectedExam(latestExam)}
                  className="text-primary-600 dark:text-primary-400 text-xs sm:text-sm font-semibold flex-shrink-0"
                >
                  {t('clients.details.view')}
                </button>
              )}
            </div>
          </div>
          {latestExam ? (
            <div className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
              <p>
                <span className="font-semibold">{t('clients.details.recorded')}:</span>{' '}
                {formatDate(latestExam.created_at)}
              </p>
              {latestExam.vital_signs && (
                <div className="grid grid-cols-2 gap-2 w-full min-w-0">
                  {Object.entries(latestExam.vital_signs).map(([key, value]) =>
                    value ? (
                      <div key={key} className="p-2 sm:p-3 rounded-xl bg-primary-50 text-primary-700 min-w-0 overflow-hidden">
                        <p className="text-xs uppercase font-semibold truncate">{key}</p>
                        <p className="text-sm sm:text-base font-bold truncate">{value}</p>
                      </div>
                    ) : null
                  )}
                </div>
              )}
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400 text-sm">{t('clients.details.noExamination')}</p>
          )}
        </Card>

        <Card variant="soft" className={`p-4 sm:p-6 ${sensitiveClass} w-full min-w-0`}>
          <div className="flex items-center gap-3 mb-4 min-w-0">
            <Heart className="text-secondary-600 dark:text-secondary-400 flex-shrink-0" />
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white truncate min-w-0">{t('clients.details.carePlans')}</h2>
          </div>
          {carePlans.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 text-sm">{t('clients.details.noCarePlans')}</p>
          ) : (
            <div className="space-y-3 max-h-60 overflow-y-auto pr-1">
              {carePlans.map((plan) => (
                <div key={plan.id} className={`p-3 rounded-xl ${plan.archived_at ? 'bg-gray-100 dark:bg-gray-800 opacity-75' : 'bg-gray-50 dark:bg-gray-800'}`}>
                  <div className="flex items-center gap-2">
                    <p className={`font-semibold flex-1 ${plan.archived_at ? 'text-gray-500 dark:text-gray-400 line-through' : 'text-gray-900 dark:text-white'}`}>
                      {plan.title}
                    </p>
                    {plan.archived_at && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded">
                        {t('carePlans.archived') || 'Archived'}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">{plan.status}</p>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card variant="soft" className={`p-4 sm:p-6 ${sensitiveClass} w-full min-w-0`}>
          <div className="flex items-center gap-3 mb-4 min-w-0">
            <Calendar className="text-accent-600 dark:text-accent-400 flex-shrink-0" />
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white truncate min-w-0">{t('clients.details.upcomingTherapies')}</h2>
          </div>
          {therapies.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 text-sm">{t('clients.details.noTherapies')}</p>
          ) : (
            <div className="space-y-3 text-sm max-h-60 overflow-y-auto pr-1">
              {therapies.map((therapy) => (
                <div key={therapy.id} className={`p-3 rounded-xl ${therapy.archived_at ? 'bg-gray-100 dark:bg-gray-800 opacity-75' : 'bg-white/60 dark:bg-gray-800'} border border-gray-100 dark:border-gray-700`}>
                  <div className="flex items-center gap-2">
                    <p className={`font-semibold flex-1 ${therapy.archived_at ? 'text-gray-500 dark:text-gray-400 line-through' : 'text-gray-900 dark:text-white'}`}>
                      {therapy.name}
                    </p>
                    {therapy.archived_at && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded">
                        {t('therapies.archived') || 'Archived'}
                      </span>
                    )}
                  </div>
                  <p className="text-gray-500 dark:text-gray-400">
                    {formatDate(therapy.scheduled_date)} • {therapy.time_of_day || 'N/A'}
                  </p>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div className="space-y-4 w-full min-w-0">
        <div className="flex flex-wrap items-center justify-between gap-3 min-w-0">
          <div className="flex items-center gap-3 min-w-0">
            <FileText className="text-primary-600 dark:text-primary-400 flex-shrink-0" />
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white truncate">{t('clients.details.examinationArchives')}</h2>
          </div>
          <Button
            variant="secondary"
            size="sm"
            className="flex items-center gap-2"
            onClick={() => setShowExamCsvTransfer((v) => !v)}
          >
            <Upload size={16} />
            CSV transfer
          </Button>
        </div>
        {showExamCsvTransfer && (
          <ExaminationImportExport
            clientId={id}
            onClose={() => setShowExamCsvTransfer(false)}
            onImportComplete={() => {
              setShowExamCsvTransfer(false)
              setRefreshKey((k) => k + 1)
            }}
          />
        )}
        {examinations.length === 0 ? (
          <Card variant="soft" className="p-4 sm:p-6 text-gray-500 dark:text-gray-400 text-sm w-full min-w-0">{t('clients.details.noExaminationsArchived')}</Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full min-w-0">
            {examinations.map((exam) => (
              <Card
                key={exam.id}
                variant="soft"
                hover
                className={`p-4 sm:p-5 cursor-pointer ${sensitiveClass} w-full min-w-0`}
                onClick={() => setSelectedExam(exam)}
              >
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-xs uppercase text-gray-400 dark:text-gray-500">{t('clients.details.recorded')}</p>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">
                      {formatDate(exam.created_at)}
                    </p>
                  </div>
                  <span className="text-primary-600 dark:text-primary-400 text-sm font-semibold">{t('clients.details.view')}</span>
                </div>
                {exam.vital_signs && (
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 text-sm text-gray-600 dark:text-gray-400 w-full">
                    {exam.vital_signs.bloodPressure && (
                      <div className="min-w-0">
                        <p className="text-xs uppercase text-gray-400 dark:text-gray-500 truncate">{t('clients.details.bloodPressure')}</p>
                        <p className="font-semibold truncate dark:text-white">{exam.vital_signs.bloodPressure}</p>
                      </div>
                    )}
                    {exam.vital_signs.heartRate && (
                      <div className="min-w-0">
                        <p className="text-xs uppercase text-gray-400 dark:text-gray-500 truncate">{t('clients.details.heartRate')}</p>
                        <p className="font-semibold truncate dark:text-white">{exam.vital_signs.heartRate} {t('clients.details.bpm')}</p>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>

      <div className="w-full min-w-0">
        <div className="flex items-center gap-3 mb-4 min-w-0">
          <Activity className="text-primary-600 dark:text-primary-400 flex-shrink-0" />
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white truncate">{t('clients.details.pastVisits')}</h2>
        </div>
        {sessions.length === 0 ? (
          <Card variant="soft" className="p-4 sm:p-6 text-gray-500 dark:text-gray-400 text-sm w-full min-w-0">
            {t('clients.details.noSessions')}
          </Card>
        ) : (
          <div className="space-y-3 max-h-96 overflow-y-auto pr-1 w-full min-w-0">
            {sessions.map((session) => (
              <Card key={session.id} variant="soft" className={`p-4 sm:p-5 ${sensitiveClass} ${session.archived_at ? 'opacity-75' : ''} w-full min-w-0`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 flex-1">
                    <p className={`text-sm font-semibold flex-1 ${session.archived_at ? 'text-gray-500 dark:text-gray-400 line-through' : 'text-gray-900 dark:text-white'}`}>
                      {formatDate(session.session_date)}
                    </p>
                    {session.archived_at && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded">
                        {t('sessions.archived') || 'Archived'}
                      </span>
                    )}
                  </div>
                  {session.duration_minutes && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">{session.duration_minutes} {t('clients.details.min')}</p>
                  )}
                </div>
                {session.focus_area && (
                  <p className="text-xs uppercase tracking-wide text-primary-600 dark:text-primary-400 font-semibold mb-2">
                    {session.focus_area}
                  </p>
                )}
                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                  {session.outcome_summary || t('clients.details.noSummary')}
                </p>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Enhanced Features Tabs */}
      <div className="space-y-6 w-full min-w-0">
        <div className="flex items-center gap-2 border-b border-gray-200 dark:border-gray-700 overflow-x-auto overflow-y-hidden pb-2 -mb-2 scrollbar-hide min-w-0 w-full" style={{ WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-3 sm:px-4 py-2 font-semibold transition-colors whitespace-nowrap flex-shrink-0 text-sm sm:text-base ${
              activeTab === 'overview'
                ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-600 dark:border-primary-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            {t('clients.details.overview')}
          </button>
          <button
            onClick={() => setActiveTab('tags')}
            className={`px-3 sm:px-4 py-2 font-semibold transition-colors flex items-center gap-2 whitespace-nowrap flex-shrink-0 text-sm sm:text-base ${
              activeTab === 'tags'
                ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-600 dark:border-primary-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <Tag size={16} className="sm:w-[18px] sm:h-[18px]" />
            {t('clients.details.tags')}
          </button>
          <button
            onClick={() => setActiveTab('communications')}
            className={`px-3 sm:px-4 py-2 font-semibold transition-colors flex items-center gap-2 whitespace-nowrap flex-shrink-0 text-sm sm:text-base ${
              activeTab === 'communications'
                ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-600 dark:border-primary-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <MessageSquare size={16} className="sm:w-[18px] sm:h-[18px]" />
            {t('clients.details.communications')}
          </button>
          <button
            onClick={() => setActiveTab('documents')}
            className={`px-3 sm:px-4 py-2 font-semibold transition-colors flex items-center gap-2 whitespace-nowrap flex-shrink-0 text-sm sm:text-base ${
              activeTab === 'documents'
                ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-600 dark:border-primary-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <FolderOpen size={16} className="sm:w-[18px] sm:h-[18px]" />
            {t('clients.details.documents')}
          </button>
          <button
            onClick={() => setActiveTab('merge')}
            className={`px-3 sm:px-4 py-2 font-semibold transition-colors flex items-center gap-2 whitespace-nowrap flex-shrink-0 text-sm sm:text-base ${
              activeTab === 'merge'
                ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-600 dark:border-primary-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <Users size={16} className="sm:w-[18px] sm:h-[18px]" />
            {t('clients.details.merge')}
          </button>
          <button
            onClick={() => setActiveTab('comparison')}
            className={`px-3 sm:px-4 py-2 font-semibold transition-colors flex items-center gap-2 whitespace-nowrap flex-shrink-0 text-sm sm:text-base ${
              activeTab === 'comparison'
                ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-600 dark:border-primary-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <GitCompare size={16} className="sm:w-[18px] sm:h-[18px]" />
            {t('clients.details.compare')}
          </button>
          <button
            onClick={() => setActiveTab('timeline')}
            className={`px-3 sm:px-4 py-2 font-semibold transition-colors flex items-center gap-2 whitespace-nowrap flex-shrink-0 text-sm sm:text-base ${
              activeTab === 'timeline'
                ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-600 dark:border-primary-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <Clock size={16} className="sm:w-[18px] sm:h-[18px]" />
            {t('clients.details.timeline')}
          </button>
          {/* Reminders tab hidden until Resend email setup is complete */}
          {/* <button
            onClick={() => setActiveTab('reminders')}
            className={`px-4 py-2 font-semibold transition-colors flex items-center gap-2 ${
              activeTab === 'reminders'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Bell size={18} />
            Reminders
          </button> */}
        </div>

        <AnimatePresence mode="wait">
          {activeTab === 'tags' && (
            <motion.div
              key="tags"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card variant="soft" className="p-4 sm:p-6 w-full min-w-0">
                <ClientTags clientId={id} selectedTags={clientTags} onChange={setClientTags} />
              </Card>
            </motion.div>
          )}

          {activeTab === 'communications' && (
            <motion.div
              key="communications"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card variant="soft" className="p-4 sm:p-6 w-full min-w-0">
                <ClientCommunications clientId={id} />
              </Card>
            </motion.div>
          )}

          {activeTab === 'documents' && (
            <motion.div
              key="documents"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card variant="soft" className="p-4 sm:p-6 w-full min-w-0">
                <ClientDocuments clientId={id} />
              </Card>
            </motion.div>
          )}

          {activeTab === 'merge' && (
            <motion.div
              key="merge"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card variant="soft" className="p-4 sm:p-6 w-full min-w-0">
                <ClientMerge
                  onMergeComplete={() => {
                    navigate('/clients')
                  }}
                />
              </Card>
            </motion.div>
          )}

          {activeTab === 'comparison' && (
            <motion.div
              key="comparison"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <div className="space-y-6 w-full min-w-0">
                {/* Examination Comparison */}
                <ExaminationComparison clientId={id} client={client} />
                
                {/* Medical History Comparison */}
                {medicalHistory && (
                  <Card variant="soft" className="p-4 sm:p-6 w-full min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Medical History Comparison</h3>
                    <HistoryComparisonView
                      clientId={id}
                      currentHistory={medicalHistory}
                      organizationId={organizationId}
                    />
                  </Card>
                )}
              </div>
            </motion.div>
          )}

          {activeTab === 'timeline' && (
            <motion.div
              key="timeline"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card variant="soft" className="p-4 sm:p-6 w-full min-w-0">
                <HistoryTimeline clientId={id} organizationId={organizationId} />
              </Card>
            </motion.div>
          )}

          {/* Reminders tab hidden until Resend email setup is complete */}
          {/* {activeTab === 'reminders' && (
            <motion.div
              key="reminders"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card variant="soft" className="p-6">
                <FollowUpReminders
                  clientId={id}
                  examinationId={latestExam?.id}
                  organizationId={organizationId}
                />
              </Card>
            </motion.div>
          )} */}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {selectedExam && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedExam(null)}
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed right-0 top-0 bottom-0 w-full max-w-full sm:max-w-3xl z-50 p-3 sm:p-4 md:p-6 overflow-y-auto bg-white dark:bg-gray-800 shadow-2xl"
            >
              <div className="flex items-center justify-between mb-6 gap-3">
                <div>
                  <p className="text-xs uppercase text-gray-400">Examination Detail</p>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                    {formatDate(selectedExam.created_at)}
                  </h3>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    className="flex items-center gap-2"
                    onClick={() => setPdfEditorOpen(true)}
                  >
                    <Download size={16} />
                    Print examination
                  </Button>
                  <button
                    onClick={() => setSelectedExam(null)}
                    className="p-2 rounded-full bg-gray-100 text-gray-500 hover:text-gray-700"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              <div className="space-y-6">
                <section>
                  <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    Vital Signs
                  </h4>
                  {selectedExam.vital_signs ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3 w-full">
                      {Object.entries(selectedExam.vital_signs).map(([key, value]) =>
                        value ? (
                          <div key={key} className="p-2 sm:p-3 rounded-xl bg-primary-50 text-primary-700 min-w-0">
                            <p className="text-xs uppercase font-semibold truncate">{key}</p>
                            <p className="text-sm sm:text-base font-bold truncate">{value}</p>
                          </div>
                        ) : null
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t('clients.details.noVitals')}</p>
                  )}
                </section>

                {selectedExam.medical_history && (
                  <section className="space-y-3">
                    <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      Medical History Snapshot
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-700 w-full">
                      {renderHistoryField('Current Medications', selectedExam.medical_history.current_medications)}
                      {renderHistoryField('Allergies', selectedExam.medical_history.allergies)}
                      {renderHistoryField('Past Surgeries', selectedExam.medical_history.past_surgeries)}
                      {renderHistoryField('Family History', selectedExam.medical_history.family_history)}
                      {renderHistoryField('Chronic Conditions', selectedExam.medical_history.chronic_conditions)}
                      {renderHistoryField('Diet', selectedExam.medical_history.diet)}
                      {renderHistoryField('Exercise', selectedExam.medical_history.exercise)}
                      {renderHistoryField('Sleep', selectedExam.medical_history.sleep)}
                      {renderHistoryField('Stress Level', selectedExam.medical_history.stress_level)}
                      {renderHistoryField('Alcohol', selectedExam.medical_history.alcohol)}
                      {renderHistoryField('Smoking', selectedExam.medical_history.smoking)}
                      {renderHistoryField('Primary Goals', selectedExam.medical_history.primary_goals)}
                      {renderHistoryField('Expectations', selectedExam.medical_history.expectations)}
                      {renderHistoryField('Previous Treatments', selectedExam.medical_history.previous_treatments)}
                    </div>
                  </section>
                )}

                <section>
                  <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    Questionnaire & Charting
                  </h4>
                  {Array.isArray(selectedExam.custom_questions) && selectedExam.custom_questions.length > 0 ? (
                    <div className="space-y-3">
                      {selectedExam.custom_questions.map((question, index) => (
                        <div key={`${question.prompt}-${index}`} className="p-4 rounded-2xl bg-gray-50">
                          <p className="text-sm font-semibold text-gray-900">{question.prompt}</p>
                          <p className="text-xs uppercase text-gray-400 mb-1">{question.type === 'choice' ? 'Structured response' : 'Open-ended'}</p>
                          <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                            {question.answer || t('clients.details.noAnswer')}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t('clients.details.noCustomQuestions')}</p>
                  )}
                </section>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <ExaminationPdfEditor
        open={pdfEditorOpen && !!selectedExam}
        onClose={() => setPdfEditorOpen(false)}
        examination={selectedExam}
        client={client}
        medicalHistory={selectedExam?.medical_history || medicalHistory}
        title="Prepare examination PDF"
      />
    </div>
  )
}

const renderHistoryField = (label, value) => {
  if (!value) return null
  
  // Handle habit arrays (diet, exercise, sleep)
  if (Array.isArray(value) && value.length > 0) {
    // Check if it's a habit array (has objects with 'text' property)
    if (value[0] && typeof value[0] === 'object' && 'text' in value[0]) {
      const habits = value.map((habit) => habit.text).filter(Boolean)
      if (habits.length === 0) return null
      
      return (
        <div className="p-3 rounded-xl bg-white/70 border border-gray-100">
          <p className="text-xs uppercase text-gray-400 mb-2">{label}</p>
          <ul className="space-y-1">
            {habits.map((habitText, index) => (
              <li key={index} className="text-sm text-gray-800 flex items-start gap-2">
                <span className="text-primary-600 mt-1">•</span>
                <span>{habitText}</span>
              </li>
            ))}
          </ul>
        </div>
      )
    }
  }
  
  // Handle string values (legacy format or other fields)
  return (
    <div className="p-3 rounded-xl bg-white/70 border border-gray-100">
      <p className="text-xs uppercase text-gray-400">{label}</p>
      <p className="text-sm text-gray-800 whitespace-pre-wrap">{value}</p>
    </div>
  )
}

export default ClientDetails

